import * as React from 'react';
import CSSMotion from 'rc-motion';
import addEventListener from 'rc-util/lib/Dom/addEventListener';
import useMergedState from 'rc-util/lib/hooks/useMergedState';
import classNames from 'classnames';
import omit from 'rc-util/lib/omit';
import { get } from 'rc-util/lib/Dom/css';
import AlignTextTop from '../_util/bonreeIcons/AlignTextTop';
import { throttleByAnimationFrame } from '../_util/throttleByAnimationFrame';
import { ConfigContext } from '../config-provider';
import getScroll from '../_util/getScroll';
import scrollTo from '../_util/scrollTo';
import { cloneElement } from '../_util/reactNode';
const BackTop = props => {
    const [visible, setVisible] = useMergedState(false, {
        value: props.visible,
    });
    const ref = React.useRef(null);
    const scrollEvent = React.useRef();
    const resizeEvent = React.useRef();
    const hasScrollBarShow = React.useRef({ virtical: false, horizontal: false });
    const getDefaultTarget = () => ref.current && ref.current.ownerDocument ? ref.current.ownerDocument : window;
    const handleScroll = throttleByAnimationFrame((e) => {
        const { visibilityHeight } = props;
        const scrollTop = getScroll(e.target, true);
        setVisible(scrollTop > visibilityHeight);
    });
    const bindScrollEvent = () => {
        const { target } = props;
        const getTarget = target || getDefaultTarget;
        const container = getTarget();
        scrollEvent.current = addEventListener(container, 'scroll', (e) => {
            handleScroll(e);
        });
        handleScroll({
            target: container,
        });
    };
    const handleResize = throttleByAnimationFrame((documentElement) => {
        if (ref.current) {
            // 纵向滚顶条
            if (window.innerWidth > documentElement.clientWidth) {
                if (!hasScrollBarShow.current.virtical) {
                    hasScrollBarShow.current.virtical = true;
                    const right = parseFloat(get(ref.current, 'right')) || 8;
                    ref.current.style.right = `${Math.abs(right - 6)}px`;
                }
            }
            else {
                hasScrollBarShow.current.virtical = false;
                ref.current.style.right = '';
            }
            // 出现横向滚动条
            if (window.innerHeight > documentElement.clientHeight) {
                if (!hasScrollBarShow.current.horizontal) {
                    hasScrollBarShow.current.horizontal = true;
                    const bottom = parseFloat(get(ref.current, 'bottom')) || 8;
                    ref.current.style.bottom = `${Math.abs(bottom - 6)}px`;
                }
            }
            else {
                hasScrollBarShow.current.horizontal = false;
                ref.current.style.bottom = '';
            }
        }
    });
    // 监控窗口尺寸变化
    const bindResize = () => {
        var _a, _b;
        const documentElement = ((_b = (_a = ref.current) === null || _a === void 0 ? void 0 : _a.ownerDocument) === null || _b === void 0 ? void 0 : _b.documentElement) || document.documentElement;
        resizeEvent.current = addEventListener(window, 'resize', () => {
            handleResize(documentElement);
        });
        handleResize(documentElement);
    };
    React.useEffect(() => {
        bindScrollEvent();
        return () => {
            if (scrollEvent.current) {
                scrollEvent.current.remove();
            }
            handleScroll.cancel();
        };
    }, [props.target]);
    React.useEffect(() => {
        if (props.autoScrollBar !== false) {
            bindResize();
        }
        return () => {
            var _a;
            (_a = resizeEvent.current) === null || _a === void 0 ? void 0 : _a.remove();
        };
    }, [props.autoScrollBar]);
    const scrollToTop = (e) => {
        const { onClick, target, duration = 450 } = props;
        scrollTo(0, {
            getContainer: target || getDefaultTarget,
            duration,
        });
        if (typeof onClick === 'function') {
            onClick(e);
        }
    };
    const renderChildren = ({ prefixCls, rootPrefixCls, }) => {
        const { children } = props;
        const defaultElement = (<div className={`${prefixCls}-content`}>
        <div className={`${prefixCls}-icon`}>
          <AlignTextTop />
        </div>
      </div>);
        return (<CSSMotion visible={visible} motionName={`${rootPrefixCls}-fade`}>
        {({ className: motionClassName }) => cloneElement(children || defaultElement, ({ className }) => ({
                className: classNames(motionClassName, className),
            }))}
      </CSSMotion>);
    };
    const { getPrefixCls, direction } = React.useContext(ConfigContext);
    const { prefixCls: customizePrefixCls, className = '' } = props;
    const prefixCls = getPrefixCls('back-top', customizePrefixCls);
    const rootPrefixCls = getPrefixCls();
    const classString = classNames(prefixCls, {
        [`${prefixCls}-rtl`]: direction === 'rtl',
    }, className);
    // fix https://fb.me/react-unknown-prop
    const divProps = omit(props, [
        'prefixCls',
        'className',
        'children',
        'visibilityHeight',
        'target',
        'visible',
    ]);
    return (<div {...divProps} className={classString} onClick={scrollToTop} ref={ref}>
      {renderChildren({ prefixCls, rootPrefixCls })}
    </div>);
};
BackTop.defaultProps = {
    visibilityHeight: 400,
};
export default React.memo(BackTop);
